import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import useHttp from './useHttp';
import useSocketIO from './useSocketIO';
import AuthContext from '../context/authContext';

const useFleet = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();
  const { on, off, emit, isConnected } = useSocketIO();

  const authHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + authCtx.token,
    }),
    [authCtx.token]
  );

  const [fleetStatuses, setFleetStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Actions that won't send a 'success' response (process dies or restarts before it can)
  const RESOLVE_ON_RUNNING = new Set([
    'pm2:restart',
    'pm2:clean-restart',
    'system:restart',
    'system:shutdown',
  ]);

  // commandId → { resolve, reject, action }
  const pendingCommands = useRef({});

  // locationId+service key → log lines array
  const [logLines, setLogLines] = useState({});

  const fetchAllStatuses = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await sendRequest({ url: '/api/v1/fleet', method: 'GET', headers: authHeaders() });
      if (data?.fleet) {
        const locations = Object.entries(data.fleet).map(([invoiceCode, services]) => ({
          invoiceCode,
          services: Object.fromEntries(
            Object.entries(services).map(([service, svcData]) => {
              const { connectionStatus, ...statusData } = svcData;
              return [service, { connectionStatus, status: statusData }];
            })
          ),
        }));
        setFleetStatuses(locations);
      }
    } catch (_) {
    } finally {
      setIsLoading(false);
    }
  }, [sendRequest, authHeaders]);

  const fetchStatusByLocation = useCallback(
    async locationId => {
      try {
        const data = await sendRequest({
          url: `/api/v1/fleet/${locationId}`,
          method: 'GET',
          headers: authHeaders(),
        });
        return data?.services ?? null;
      } catch (_) {
        return null;
      }
    },
    [sendRequest, authHeaders]
  );

  const getDeploymentHistory = useCallback(
    async (locationId, service) => {
      try {
        const data = await sendRequest({
          url: `/api/v1/fleet/${locationId}/${service}/history`,
          method: 'GET',
          headers: authHeaders(),
        });
        return data?.history ?? [];
      } catch (_) {
        return [];
      }
    },
    [sendRequest, authHeaders]
  );

  // Send a fleet command and wait for the response via Socket.io
  const sendCommand = useCallback(
    (locationId, service, action, params = {}) => {
      return new Promise(async (resolve, reject) => {
        try {
          const data = await sendRequest({
            url: `/api/v1/fleet/${locationId}/${service}/command`,
            method: 'POST',
            body: { action, params },
            headers: authHeaders(),
          });
          if (!data?.commandId) {
            reject(new Error('Kein commandId in der Antwort'));
            return;
          }
          const { commandId } = data;

          // Register pending command listener
          pendingCommands.current[commandId] = { resolve, reject, action };

          // Join the Socket.io room for this command
          emit('join_fleet_cmd', { commandId });

          // Timeout after 2 minutes
          setTimeout(() => {
            if (pendingCommands.current[commandId]) {
              delete pendingCommands.current[commandId];
              reject(new Error('Timeout: Keine Antwort vom Gerät erhalten.'));
            }
          }, 120_000);
        } catch (err) {
          reject(err);
        }
      });
    },
    [sendRequest, emit, authHeaders]
  );

  const startLogStream = useCallback(
    async (locationId, service) => {
      await sendRequest({
        url: `/api/v1/fleet/${locationId}/${service}/logs/start`,
        method: 'POST',
        headers: authHeaders(),
      });
      emit('join_fleet_logs', { invoiceCode: locationId, service });
      setLogLines(prev => ({ ...prev, [`${locationId}:${service}`]: [] }));
    },
    [sendRequest, emit, authHeaders]
  );

  const stopLogStream = useCallback(
    async (locationId, service) => {
      await sendRequest({
        url: `/api/v1/fleet/${locationId}/${service}/logs/stop`,
        method: 'POST',
        headers: authHeaders(),
      });
    },
    [sendRequest, authHeaders]
  );

  const removeDevice = useCallback(
    async (locationId, service) => {
      await sendRequest({
        url: `/api/v1/fleet/${locationId}/${service}`,
        method: 'DELETE',
        headers: authHeaders(),
      });
      await fetchAllStatuses();
    },
    [sendRequest, authHeaders, fetchAllStatuses]
  );

  // Handle fleet command responses via Socket.io
  useEffect(() => {
    const handleFleetResponse = ({ commandId, status, message, ...rest }) => {
      const pending = pendingCommands.current[commandId];
      if (!pending) return;
      if (status === 'running') {
        // For commands where the process dies/restarts, 'running' is the final response
        if (RESOLVE_ON_RUNNING.has(pending.action)) {
          delete pendingCommands.current[commandId];
          pending.resolve({ status: 'success', message, ...rest });
        }
        return;
      }
      delete pendingCommands.current[commandId];
      if (status === 'success') {
        pending.resolve({ status, message, ...rest });
      } else {
        pending.reject(new Error(message || 'Befehl fehlgeschlagen'));
      }
    };

    const handleFleetLog = ({ line, invoiceCode, service }) => {
      const key = `${invoiceCode}:${service}`;
      setLogLines(prev => ({
        ...prev,
        [key]: [...(prev[key] ?? []).slice(-499), line],
      }));
    };

    on('fleetCommandResponse', handleFleetResponse);
    on('fleetLog', handleFleetLog);
    return () => {
      off('fleetCommandResponse', handleFleetResponse);
      off('fleetLog', handleFleetLog);
    };
  }, [on, off]);

  return {
    fleetStatuses,
    isLoading,
    isSocketConnected: isConnected,
    fetchAllStatuses,
    fetchStatusByLocation,
    getDeploymentHistory,
    sendCommand,
    startLogStream,
    stopLogStream,
    removeDevice,
    logLines,
  };
};

export default useFleet;
